import { useState, useEffect } from 'react';
import { useSEO } from '../lib/useSEO';
import { Link, useNavigate } from 'react-router-dom';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Progress } from '../components/ui/progress';
import { 
  Brain, ArrowLeft, Trophy, Target, TrendingUp, TrendingDown,
  Minus, BookOpen, Lightbulb, BarChart3, Sparkles, ChevronRight,
  CheckCircle2, AlertTriangle, Clock
} from 'lucide-react';
import { useAuthStore } from '../lib/auth';
import api from '../lib/api';
import {
  RadarChart,
  PolarGrid,
  PolarAngleAxis,
  PolarRadiusAxis,
  Radar,
  ResponsiveContainer,
  LineChart,
  Line,
  XAxis,
  YAxis,
  Tooltip,
  CartesianGrid
} from 'recharts';

const LOGO_URL = "https://customer-assets.emergentagent.com/job_trivia-saas/artifacts/9y2lje2h_Jep_Prep_Logo.png";

const TREND_ICONS = {
  up: TrendingUp,
  down: TrendingDown,
  stable: Minus
};

const TREND_COLORS = {
  up: 'text-emerald-400',
  down: 'text-red-400',
  stable: 'text-gray-400'
};

const PRIORITY_COLORS = {
  high: 'bg-red-500/20 text-red-400 border-red-500/30',
  medium: 'bg-yellow-500/20 text-yellow-400 border-yellow-500/30',
  low: 'bg-blue-500/20 text-blue-400 border-blue-500/30'
};

export default function KnowledgePortfolio() {
  useSEO('Knowledge Portfolio | JepPrep', 'Your personalized trivia knowledge map. Track strengths, weaknesses, and get AI study recommendations.');
  const navigate = useNavigate();
  const { user } = useAuthStore();
  
  const [summary, setSummary] = useState(null);
  const [categories, setCategories] = useState([]);
  const [radarData, setRadarData] = useState([]);
  const [recommendations, setRecommendations] = useState([]);
  const [progress, setProgress] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('overview');

  useEffect(() => {
    if (user) {
      fetchPortfolioData();
    }
  }, [user]);

  const fetchPortfolioData = async () => {
    try {
      setLoading(true);
      const [summaryRes, categoriesRes, radarRes, recsRes, progressRes] = await Promise.all([
        api.get(`/api/v1/askalex/portfolio/summary?user_id=${user.id}`),
        api.get(`/api/v1/askalex/portfolio/categories?user_id=${user.id}`),
        api.get(`/api/v1/askalex/portfolio/radar?user_id=${user.id}`),
        api.get(`/api/v1/askalex/portfolio/recommendations?user_id=${user.id}`),
        api.get(`/api/v1/askalex/portfolio/progress?user_id=${user.id}&days=30`)
      ]);
      
      setSummary(summaryRes.data);
      setCategories(categoriesRes.data.categories || []);
      setRadarData(radarRes.data.radar_data || []);
      setRecommendations(recsRes.data.recommendations || []);
      setProgress(progressRes.data.progress || []);
    } catch (error) {
      console.error('Error fetching portfolio data:', error);
    } finally {
      setLoading(false);
    }
  };

  const getScoreColor = (score) => {
    if (score >= 80) return 'text-emerald-400';
    if (score >= 60) return 'text-yellow-300';
    if (score >= 40) return 'text-amber-400';
    return 'text-red-400';
  };

  const getScoreLabel = (score) => {
    if (score >= 80) return 'Mastered';
    if (score >= 60) return 'Proficient';
    if (score >= 40) return 'Learning';
    return 'Needs Work';
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[hsl(var(--background))] flex items-center justify-center">
        <div className="text-white text-xl">Loading your Knowledge Portfolio...</div>
      </div>
    );
  }

  const hasData = summary && summary.total_clues_answered > 0;

  return (
    <div className="min-h-screen bg-[hsl(var(--background))]">
      {/* Header */}
      <header className="bg-[hsl(var(--secondary))] border-b border-[hsl(var(--border))]">
        <div className="max-w-7xl mx-auto px-3 sm:px-4 py-3 sm:py-4">
          <div className="flex items-center justify-between gap-2">
            <div className="flex items-center gap-2 sm:gap-4 min-w-0">
              <Link to="/askalex">
                <Button variant="ghost" size="icon" className="text-white hover:bg-white/10 shrink-0">
                  <ArrowLeft className="w-5 h-5" />
                </Button>
              </Link>
              <img src={LOGO_URL} alt="JepPrep" className="h-8 sm:h-12 hidden sm:block" />
              <div className="min-w-0">
                <h1 className="text-base sm:text-xl font-display text-white truncate">Knowledge Portfolio</h1>
                <p className="text-gray-400 text-xs hidden sm:block">Track your trivia mastery</p>
              </div>
            </div>
            <Link to="/askalex/beatthedata">
              <Button className="bg-[hsl(var(--brand-gold-500))] text-[hsl(var(--brand-blue-900))] hover:bg-[hsl(var(--brand-blue-800))] hover:text-white text-xs sm:text-sm px-2 sm:px-4 shrink-0">
                <Target className="w-3 h-3 sm:w-4 sm:h-4 mr-1 sm:mr-2" />
                Practice
              </Button>
            </Link>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-3 sm:px-4 py-4 sm:py-8">
        {!hasData ? (
          /* Empty State */
          <Card className="bg-[hsl(var(--secondary))] border-[hsl(var(--border))]">
            <CardContent className="p-12 text-center">
              <div className="w-20 h-20 rounded-full bg-[hsl(var(--brand-gold-500))]/20 flex items-center justify-center mx-auto mb-6">
                <BookOpen className="w-10 h-10 text-[hsl(var(--brand-gold-500))]" />
              </div>
              <h2 className="text-2xl font-bold text-white mb-2">Start Building Your Portfolio</h2>
              <p className="text-gray-400 mb-8 max-w-md mx-auto">
                Play Beat the Data to track your performance across different trivia categories and get personalized study recommendations.
              </p>
              <Link to="/askalex/beatthedata">
                <Button className="bg-[hsl(var(--brand-gold-500))] text-[hsl(var(--brand-blue-900))] hover:bg-[hsl(var(--brand-blue-800))] hover:text-white px-8 py-6 text-lg">
                  Start Playing
                  <ChevronRight className="w-5 h-5 ml-2" />
                </Button>
              </Link>
            </CardContent>
          </Card>
        ) : (
          <div className="space-y-6">
            {/* Summary Cards */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <Card className="bg-[hsl(var(--brand-blue-800))] border-[hsl(var(--border))]">
                <CardContent className="p-4 text-center">
                  <Trophy className="w-8 h-8 mx-auto mb-2 text-[hsl(var(--brand-gold-500))]" />
                  <p className={`text-3xl font-bold ${getScoreColor(summary.overall_score)}`}>
                    {summary.overall_score}
                  </p>
                  <p className="text-gray-400 text-sm">Overall Score</p>
                </CardContent>
              </Card>
              
              <Card className="bg-[hsl(var(--brand-blue-800))] border-[hsl(var(--border))]">
                <CardContent className="p-4 text-center">
                  <CheckCircle2 className="w-8 h-8 mx-auto mb-2 text-emerald-400" />
                  <p className="text-3xl font-bold text-white">{summary.mastered_categories}</p>
                  <p className="text-gray-400 text-sm">Mastered</p>
                </CardContent>
              </Card>
              
              <Card className="bg-[hsl(var(--brand-blue-800))] border-[hsl(var(--border))]">
                <CardContent className="p-4 text-center">
                  <AlertTriangle className="w-8 h-8 mx-auto mb-2 text-amber-400" />
                  <p className="text-3xl font-bold text-white">{summary.needs_work_categories}</p>
                  <p className="text-gray-400 text-sm">Needs Work</p>
                </CardContent>
              </Card>
              
              <Card className="bg-[hsl(var(--brand-blue-800))] border-[hsl(var(--border))]">
                <CardContent className="p-4 text-center">
                  <Target className="w-8 h-8 mx-auto mb-2 text-cyan-400" />
                  <p className="text-3xl font-bold text-white">{summary.overall_accuracy}%</p>
                  <p className="text-gray-400 text-sm">Accuracy</p>
                </CardContent>
              </Card>
            </div>

            <div className="grid lg:grid-cols-2 gap-6">
              {/* Radar Chart */}
              <Card className="bg-[hsl(var(--secondary))] border-[hsl(var(--border))]">
                <CardContent className="p-6">
                  <div className="flex items-center gap-3 mb-6">
                    <div className="w-10 h-10 rounded-lg bg-purple-500/20 flex items-center justify-center">
                      <BarChart3 className="w-5 h-5 text-purple-400" />
                    </div>
                    <div>
                      <h3 className="text-white font-bold">Skill Radar</h3>
                      <p className="text-gray-400 text-sm">Your strengths and weaknesses</p>
                    </div>
                  </div>
                  
                  <div className="h-[300px]">
                    <ResponsiveContainer width="100%" height="100%">
                      <RadarChart data={radarData}>
                        <PolarGrid stroke="hsl(222, 47%, 25%)" />
                        <PolarAngleAxis 
                          dataKey="category" 
                          tick={{ fill: '#9ca3af', fontSize: 11 }}
                        />
                        <PolarRadiusAxis 
                          angle={30} 
                          domain={[0, 100]} 
                          tick={{ fill: '#6b7280', fontSize: 10 }}
                        />
                        <Radar
                          name="Score"
                          dataKey="score"
                          stroke="hsl(45, 93%, 47%)"
                          fill="hsl(45, 93%, 47%)"
                          fillOpacity={0.3}
                          strokeWidth={2}
                        />
                      </RadarChart>
                    </ResponsiveContainer>
                  </div>
                </CardContent>
              </Card>

              {/* AI Recommendations */}
              <Card className="bg-[hsl(var(--secondary))] border-[hsl(var(--border))]">
                <CardContent className="p-6">
                  <div className="flex items-center gap-3 mb-6">
                    <div className="w-10 h-10 rounded-lg bg-[hsl(var(--brand-gold-500))]/20 flex items-center justify-center">
                      <Lightbulb className="w-5 h-5 text-[hsl(var(--brand-gold-500))]" />
                    </div>
                    <div>
                      <h3 className="text-white font-bold">Study Recommendations</h3>
                      <p className="text-gray-400 text-sm">AI-powered suggestions</p>
                    </div>
                  </div>
                  
                  <div className="space-y-4">
                    {recommendations.length > 0 ? (
                      recommendations.map((rec, index) => (
                        <div 
                          key={rec.category || `rec-${index}`}
                          className={`p-4 rounded-xl border ${PRIORITY_COLORS[rec.priority] || PRIORITY_COLORS.low}`}
                        >
                          <div className="flex items-start justify-between mb-2">
                            <Badge className={PRIORITY_COLORS[rec.priority]}>
                              {rec.priority.toUpperCase()}
                            </Badge>
                            <span className="text-gray-400 text-sm">{rec.category}</span>
                          </div>
                          <p className="text-white text-sm mb-2">{rec.recommendation}</p>
                          <p className="text-gray-400 text-xs">{rec.suggested_action}</p>
                        </div>
                      ))
                    ) : (
                      <div className="text-center py-8">
                        <Sparkles className="w-8 h-8 text-gray-600 mx-auto mb-2" />
                        <p className="text-gray-400">Play more to get personalized recommendations</p>
                      </div>
                    )}
                  </div>
                </CardContent>
              </Card>
            </div>

            {/* Progress Over Time */}
            {progress.length > 0 && (
              <Card className="bg-[hsl(var(--secondary))] border-[hsl(var(--border))]">
                <CardContent className="p-6">
                  <div className="flex items-center gap-3 mb-6">
                    <div className="w-10 h-10 rounded-lg bg-cyan-500/20 flex items-center justify-center">
                      <TrendingUp className="w-5 h-5 text-cyan-400" />
                    </div>
                    <div>
                      <h3 className="text-white font-bold">Progress Over Time</h3>
                      <p className="text-gray-400 text-sm">Last 30 days</p>
                    </div>
                  </div>
                  
                  <div className="h-[200px]">
                    <ResponsiveContainer width="100%" height="100%">
                      <LineChart data={progress}>
                        <CartesianGrid strokeDasharray="3 3" stroke="hsl(222, 47%, 25%)" />
                        <XAxis 
                          dataKey="date" 
                          stroke="#6b7280"
                          tick={{ fontSize: 11 }}
                          tickFormatter={(value) => value.split('-').slice(1).join('/')}
                        />
                        <YAxis 
                          stroke="#6b7280"
                          domain={[0, 100]}
                          tick={{ fontSize: 11 }}
                        />
                        <Tooltip 
                          contentStyle={{ 
                            backgroundColor: 'hsl(222, 47%, 15%)', 
                            border: '1px solid hsl(222, 47%, 25%)',
                            borderRadius: '8px'
                          }}
                          labelStyle={{ color: '#fff' }}
                        />
                        <Line 
                          type="monotone" 
                          dataKey="cumulative_accuracy" 
                          stroke="hsl(45, 93%, 47%)" 
                          strokeWidth={2}
                          dot={{ fill: 'hsl(45, 93%, 47%)', strokeWidth: 0 }}
                          name="Cumulative Accuracy"
                        />
                        <Line 
                          type="monotone" 
                          dataKey="daily_accuracy" 
                          stroke="hsl(190, 90%, 50%)" 
                          strokeWidth={2}
                          dot={{ fill: 'hsl(190, 90%, 50%)', strokeWidth: 0 }}
                          name="Daily Accuracy"
                          strokeDasharray="5 5"
                        />
                      </LineChart>
                    </ResponsiveContainer>
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Category Breakdown */}
            <Card className="bg-[hsl(var(--secondary))] border-[hsl(var(--border))]">
              <CardContent className="p-6">
                <div className="flex items-center gap-3 mb-6">
                  <div className="w-10 h-10 rounded-lg bg-emerald-500/20 flex items-center justify-center">
                    <BookOpen className="w-5 h-5 text-emerald-400" />
                  </div>
                  <div>
                    <h3 className="text-white font-bold">Category Breakdown</h3>
                    <p className="text-gray-400 text-sm">Detailed performance by topic</p>
                  </div>
                </div>
                
                <div className="space-y-4">
                  {categories.map((cat, index) => {
                    const TrendIcon = TREND_ICONS[cat.trend] || Minus;
                    return (
                      <div 
                        key={cat.category || `cat-${index}`}
                        className="bg-[hsl(var(--brand-blue-800))] rounded-xl p-4"
                      >
                        <div className="flex items-center justify-between mb-3">
                          <div className="flex items-center gap-3">
                            <span className="text-white font-medium">{cat.category}</span>
                            <TrendIcon className={`w-4 h-4 ${TREND_COLORS[cat.trend]}`} />
                          </div>
                          <div className="flex items-center gap-4">
                            <span className="text-gray-400 text-sm">
                              {cat.correct}/{cat.total} correct
                            </span>
                            <Badge className={`${getScoreColor(cat.score)} bg-transparent`}>
                              {getScoreLabel(cat.score)}
                            </Badge>
                          </div>
                        </div>
                        
                        <div className="flex items-center gap-4">
                          <div className="flex-1">
                            <Progress 
                              value={cat.score} 
                              className="h-2"
                            />
                          </div>
                          <span className={`text-lg font-bold ${getScoreColor(cat.score)}`}>
                            {cat.score}
                          </span>
                        </div>
                        
                        {cat.subcategories && cat.subcategories.length > 0 && (
                          <div className="mt-2 flex flex-wrap gap-1">
                            {cat.subcategories.map((sub, i) => (
                              <span key={sub} className="text-xs text-gray-500 bg-[hsl(var(--secondary))] px-2 py-0.5 rounded">
                                {sub}
                              </span>
                            ))}
                          </div>
                        )}
                      </div>
                    );
                  })}
                  
                  {categories.length === 0 && (
                    <div className="text-center py-8">
                      <BookOpen className="w-8 h-8 text-gray-600 mx-auto mb-2" />
                      <p className="text-gray-400">No category data yet</p>
                    </div>
                  )}
                </div>
              </CardContent>
            </Card>
          </div>
        )}
      </main>
    </div>
  );
}
